import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Text } from '@/components/ui/text/Text';
import { Tooltip } from '@/components/ui/tooltips/Tooltip';
import { Checkbox } from '@/components/ui/inputs/Checkbox';
import { colors, zIndex } from '@/assets/styles';
import { useSelectorOptionTooltip } from '@/hooks/ui/selector';
import type { SelectorOption } from './Selector.types';


export interface SelectorOptionItemProps {
  option: SelectorOption;
  index: number;
  baseId: string;
  selected: boolean;
  active: boolean;
  isMultiple: boolean;
  onToggle: (value: string) => void;
  onMouseEnter: () => void;
}

export const SelectorOptionItem: React.FC<SelectorOptionItemProps> = ({
  option,
  index,
  baseId,
  selected,
  active,
  isMultiple,
  onToggle,
  onMouseEnter,
}) => {
  const {
    ref: tooltipRef,
    showTooltip,
    isTruncated,
    tooltipId,
    handleMouseEnter: handleTooltipMouseEnter,
    handleMouseLeave: handleTooltipMouseLeave,
  } = useSelectorOptionTooltip();

  const liRef = useRef<HTMLLIElement | null>(null);
  const [showDisabledTooltip, setShowDisabledTooltip] = useState(false);

  const baseColor = option.disabled ? colors.grays.neutral99 : colors.primary.main;
  let background: string;
  if (selected) {
    background = colors.primary.dash;
  } else if (active) {
    background = colors.primary.background200;
  } else {
    background = 'transparent';
  }
  const textColor = selected ? colors.grays.neutralFF : baseColor;

  const handleClick = () => {
    if (!option.disabled) onToggle(option.value);
  };

  const handleMouseEnterCombined = () => {
    onMouseEnter();
    if (option.disabled && option.disabledTooltip) {
      setShowDisabledTooltip(true);
    }
    handleTooltipMouseEnter();
  };

  const handleMouseLeaveCombined = () => {
    setShowDisabledTooltip(false);
    handleTooltipMouseLeave();
  };

  const defaultOptionContent = (
    <>
      {isMultiple && (
        <span className="pt-0.5 flex-shrink-0">
          <Checkbox
            checked={selected}
            onChange={() => onToggle(option.value)}
            disabled={option.disabled}
            className="!m-0"
            boxClassName="!m-0"
          />
        </span>
      )}
      <div className="flex flex-col flex-1 min-w-0">
        <div
          ref={tooltipRef}
          className="overflow-hidden text-ellipsis whitespace-nowrap"
          style={{ width: '100%', maxWidth: '100%' }}
        >
          <Text
            variant="subheader-regular"
            color={textColor}
          >
            {option.label}
          </Text>
        </div>
        {option.supportingText && (
          <Text
            variant="small-regular"
            color={selected ? colors.grays.neutralFF : colors.grays.neutral66}
            className="truncate"
          >
            {option.supportingText}
          </Text>
        )}
      </div>
    </>
  );

  return (
    <li
      ref={liRef}
      key={option.value}
      id={`${baseId}-opt-${index}`}
      role="option"
      aria-selected={selected}
      data-idx={index}
      tabIndex={-1}
      onClick={handleClick}
      onMouseEnter={handleMouseEnterCombined}
      onMouseLeave={handleMouseLeaveCombined}
      className={`pl-4 pr-2 py-2 flex items-start gap-2 border-b last:border-b-0 ${
        option.disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
      }`}
      style={{
        backgroundColor: background,
        color: textColor,
        borderColor: colors.primary.background100,
      }}
    >
      {defaultOptionContent}
      {showDisabledTooltip && typeof document !== 'undefined' && ReactDOM.createPortal(
        <Tooltip
          body={option.disabledTooltip!}
          anchorRef={liRef}
          strategy="auto"
          maxWidth={280}
          style={{ zIndex: zIndex.sidePanel + 5 }}
        />,
        document.body,
      )}
      {showTooltip && isTruncated && (
        <Tooltip
          id={tooltipId}
          body={option.label}
          anchorRef={tooltipRef}
          strategy="auto"
        />
      )}
    </li>
  );
};
